import clsx from 'clsx';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import QuestionIcon from '@/assets/question.svg';
import { Button, HelpContainer } from '@/components/Elements';
import { ConfirmDialog } from '@/components/Elements/ConfirmDialog';
import { defaultModalStyles, extraWideModalStyles } from '@/components/Elements/Modal/customStyles';
import { PaymentsModal } from '@/components/Elements/PaymentsModal';
import { Tooltip } from '@/components/Elements/Tooltip';
import { PATH_UPDATE_PAYMENT_METHOD } from '@/config';
import {
  ACTIVE,
  CANCELLED,
  DEFAULT_CURRENCY_RESOLUTION_FOR_METERED,
  INCOMPLETE,
  INTERVAL_COUNT_FOR_BILLING_MONTH,
  PROCESSING,
  USAGE_LICENSED,
  USAGE_METERED,
  USD,
} from '@/config/appConstants';
import { useMe } from '@/hooks/useMe';
import {
  Currency,
  InvoicePreviewTypes,
  PricesTypes,
  ProductTypes,
  UserProductTypes,
} from '@/types';
import {
  filterUserProductsByStatus,
  getBillingModeFromUserProduct,
  getBillingModePlanText,
  getProductPrice,
  getUserCurrentProductFromProducts,
  isAppSumo,
  isExternalPaidProduct,
  isFreemiumProduct,
  parseCurrency,
  parseDate,
} from '@/utils/helpers';

export type CurrentUserPlanContainerProps = {
  userCurrency: Currency;
  productType: string;
  onPlanCancel: (product: ProductTypes | null | undefined) => void;
  onPlanReactivate: (product: ProductTypes | null | undefined) => void;
  onAppsumoAction: () => void;
  onRequestInfo: () => void;
  onCancelIncompleteAction?: () => void;
  userProducts: UserProductTypes[];
};

export const CurrentUserPlanContainer = ({
  userProducts,
  userCurrency,
  onPlanCancel,
  onPlanReactivate,
  onAppsumoAction,
  onRequestInfo,
  onCancelIncompleteAction,
  productType,
}: CurrentUserPlanContainerProps) => {
  const { t, i18n } = useTranslation();

  const { getMe } = useMe();
  const me = getMe();
  const [cancelConfirmModalIsOpen, setCancelConfirmModalIsOpen] = useState(false);
  const [reactivateConfirmModalIsOpen, setReactivateConfirmModalIsOpen] = useState(false);
  const [disabledCancelButton, setDisabledCancelButton] = useState(false);

  const userProduct = getUserCurrentProductFromProducts(userProducts) as UserProductTypes;
  const product = userProduct.product;
  const userBillingMode = getBillingModeFromUserProduct(userProduct);
  const isCancelled = !!filterUserProductsByStatus(userProducts, CANCELLED);
  const isProcessing = !!filterUserProductsByStatus(userProducts, PROCESSING);
  const isIncomplete = !!filterUserProductsByStatus(userProducts, INCOMPLETE);
  const isActive = !!filterUserProductsByStatus(userProducts, ACTIVE);
  const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);
  const userProductProcessing = filterUserProductsByStatus(userProducts || [], PROCESSING);

  userCurrency = isAppSumo(me) ? USD : userCurrency;

  const getItemListStyle = () => 'flex justify-between border-b border-gray-200 py-5';
  const getItemLabelStyle = () => 'text-md-blue text-sm';

  const getOnCancelAction = () => {
    if (isAppSumo(me) && me.capabilities.appsumo.canMakeAppsumoActions) {
      return onAppsumoAction;
    }
    if (isExternalPaidProduct(userProducts)) {
      return onRequestInfo;
    }
    if (isIncomplete) return onDefaultCancelIncompleteAction;

    return requestCancellationConfirm;
  };

  const getOnChangeCreditCardAction = () => window.location.assign(PATH_UPDATE_PAYMENT_METHOD);

  const getUpdateAppsumoAction = () => {
    if (isAppSumo(me)) {
      return onAppsumoAction;
    }
    return onRequestInfo;
  };

  const requestCancellationConfirm = () => {
    setCancelConfirmModalIsOpen(true);
  };

  const requestReactivateConfirm = () => {
    setReactivateConfirmModalIsOpen(true);
  };

  const onDefaultCancelIncompleteAction = async () => {
    setDisabledCancelButton(true);
    onCancelIncompleteAction && (await onCancelIncompleteAction());
    setDisabledCancelButton(false);
  };

  const getReactiveProductButton = () => {
    return (
      <div className="flex justify-end mr-4 mb-5">
        <Button onClick={requestReactivateConfirm} variant="primary" className="uppercase">
          {t('MyRatePage.reactivateProduct')}
        </Button>
      </div>
    );
  };

  const getActionProductButton = (
    userProducts: UserProductTypes[],
    userProductActive: UserProductTypes | null
  ) => {
    if (isCancelled && !isIncomplete) {
      return getReactiveProductButton();
    }

    if (isProcessing || (userProductActive && isFreemiumProduct(userProductActive.product))) {
      return;
    }

    const buttonLabel = t(
      isExternalPaidProduct(userProducts)
        ? 'MyRatePage.cancelProductRequest'
        : isIncomplete
        ? 'Forms.cancelPendingProduct'
        : 'MyRatePage.cancelProduct'
    );

    const changeCreditCardLabel = t(
      !isExternalPaidProduct(userProducts) ? 'MyRatePage.viewOrChangeCreditCard' : ''
    );

    const updateAppsumoButtonLabel = t(
      userProductActive && isAppSumo(me) && me.capabilities.appsumo.canMakeAppsumoActions
        ? 'MyRatePage.updateProductRequest'
        : ''
    );

    return (
      <div
        className={clsx('flex justify-end mr-4 mb-5', {
          'justify-between ml-5':
            me.capabilities.appsumo.canMakeAppsumoActions || !isExternalPaidProduct(userProducts),
        })}
      >
        {changeCreditCardLabel && (
          <Button onClick={getOnChangeCreditCardAction} variant="secondary" className="uppercase">
            {changeCreditCardLabel}
          </Button>
        )}

        {updateAppsumoButtonLabel && (
          <Button onClick={getUpdateAppsumoAction()} variant="secondary" className="uppercase">
            {updateAppsumoButtonLabel}
          </Button>
        )}

        <Button
          onClick={getOnCancelAction()}
          variant="danger"
          className="uppercase"
          disabled={disabledCancelButton}
        >
          {buttonLabel}
        </Button>
      </div>
    );
  };

  const onConfirmCancelPlan = () => {
    setCancelConfirmModalIsOpen(false);
    onPlanCancel(product);
  };

  const onConfirmReactivatePlan = () => {
    setReactivateConfirmModalIsOpen(false);
    onPlanReactivate(product);
  };

  const onCancelCancelPlan = () => {
    setCancelConfirmModalIsOpen(false);
  };

  const onCancelReactivatePlan = () => {
    setReactivateConfirmModalIsOpen(false);
  };

  const getCancelConfirmDialog = () => {
    const getCancelConfirmDescription = () => {
      return (
        <div className="modal__content">
          <p>{t('MyRatePage.cancelProductWarningTitle')}</p>
          <p>{t('MyRatePage.cancelProductWarningDescription')}</p>
        </div>
      );
    };

    return (
      <ConfirmDialog
        title={<h2 className="md__payments__modal__title--h2">{t('MyRatePage.cancelProduct')}</h2>}
        description={getCancelConfirmDescription()}
        actionButton={t('MyRatePage.cancelProduct')}
        cancelButton={t('MyRatePage.continueWithProduct')}
        onActionButton={onConfirmCancelPlan}
        onCancelButton={onCancelCancelPlan}
        onActionButtonVariant="secondary"
        onCancelButtonVariant="ghost"
      />
    );
  };

  const getReactivateConfirmDialog = () => {
    const getReactivateConfirmDescription = () => {
      return (
        <div>
          <p className="font-bold text-lg">{t('MyRatePage.reactivateProductWarningTitle')}</p>
        </div>
      );
    };

    return (
      <ConfirmDialog
        title={
          <h2 className="md__payments__modal__title--h2">{t('MyRatePage.reactivateProduct')}</h2>
        }
        description={getReactivateConfirmDescription()}
        actionButton={t('MyRatePage.reactivateProductShort')}
        actionButtonToRight={true}
        onActionButton={onConfirmReactivatePlan}
        onCancelButton={onCancelReactivatePlan}
      />
    );
  };

  const getTooltip = (price: PricesTypes | false) => {
    if (!price) {
      return;
    }

    const getTooltipMessage = () => (
      <>
        <p>{t('MyRatePage.notExcessToolTip')}</p>
        <p>{t('MyRatePage.notExcessToolTip2')}</p>
      </>
    );

    return (
      <Tooltip
        message={getTooltipMessage()}
        className="ml-1"
        trigger="click"
        tooltipClassName="w-[300px] bg-blue-100 text-blue-900 border-blue-200 px-3 pt-2 pb-4"
      >
        <img
          className="w-5 h-5 text-md-links"
          src={QuestionIcon}
          alt="Question"
          width="20"
          height="20"
        />
      </Tooltip>
    );
  };

  const getTooltipPriceInvoicePreview = () => {
    const getTooltipMessage = () => (
      <>
        <p>{t('MyRatePage.nextPriceInvoiceToolTip')}</p>
      </>
    );

    return (
      <Tooltip
        message={getTooltipMessage()}
        className="ml-1"
        trigger="click"
        tooltipClassName="w-[300px] bg-blue-100 text-blue-900 border-blue-200 px-3 pt-2 pb-4"
      >
        <img
          className="w-5 h-5 text-md-links"
          src={QuestionIcon}
          alt="Question"
          width="20"
          height="20"
        />
      </Tooltip>
    );
  };

  const renderPrice = (price: PricesTypes | false) => {
    if (!price) {
      const parsedPrice = parseCurrency(i18n.language, userCurrency, 0);
      return `${parsedPrice} / ${t('BillingMode.month')}`;
    }
    const parsedPrice = parseCurrency(i18n.language, userCurrency, price.amount as number | bigint);
    const label =
      price?.recurring?.interval_count == INTERVAL_COUNT_FOR_BILLING_MONTH
        ? t('BillingMode.month')
        : t('BillingMode.year');

    const labelMonth = !isAppSumo(me) ? ` / ${label}` : '';

    return `${parsedPrice}${labelMonth}`;
  };

  const renderMeteredPrice = (price: PricesTypes | false, productType: string) => {
    if (!price) {
      return t('MyRatePage.notExcess');
    }

    const parsedPrice = parseCurrency(
      i18n.language,
      userCurrency,
      price.amount as number | bigint,
      DEFAULT_CURRENCY_RESOLUTION_FOR_METERED
    );

    return `${parsedPrice} / ${productType}`;
  };

  const productPriceObj = getProductPrice(
    product,
    USAGE_LICENSED,
    userCurrency,
    userBillingMode
  ) as PricesTypes;

  const productPriceExcess = getProductPrice(
    product,
    USAGE_METERED,
    userCurrency,
    userBillingMode
  ) as PricesTypes;

  const renderPeriodEndDateInvoicePreview = (periodEndDate: string | undefined) => {
    return (
      periodEndDate && (
        <div className={getItemListStyle()}>
          <p className={getItemLabelStyle()}>{t('MyRatePage.nextDateInvoice')}</p>
          <p className={getItemLabelStyle()}>{parseDate(i18n.language, periodEndDate)}</p>
        </div>
      )
    );
  };

  const renderPriceInvoicePreview = (invoicePreview: InvoicePreviewTypes | null) => {
    return (
      typeof invoicePreview?.amountDue !== 'undefined' && (
        <div className={getItemListStyle()}>
          <div className={clsx(getItemLabelStyle(), 'flex justify-end items-center')}>
            {t('MyRatePage.nextPriceInvoice')}
            {getTooltipPriceInvoicePreview()}
          </div>
          <p className={getItemLabelStyle()}>
            {parseCurrency(
              i18n.language,
              invoicePreview.currency,
              invoicePreview?.amountDue as number | bigint
            )}
          </p>
        </div>
      )
    );
  };

  const renderEmailExcessInvoicePreview = (emailExcess: number | undefined | null) => {
    return (
      (emailExcess && (
        <div className={getItemListStyle()}>
          <p className={getItemLabelStyle()}>{t('MyRatePage.emailExcessInvoice')}</p>
          <p className={getItemLabelStyle()}>
            {new Intl.NumberFormat(i18n.language).format(emailExcess)}
          </p>
        </div>
      )) ||
      ''
    );
  };

  const renderInvoicePreview = (userProduct: UserProductTypes) => {
    return (
      <>
        {renderPeriodEndDateInvoicePreview(userProduct.invoicePreview?.periodEndDate)}
        {renderPriceInvoicePreview(userProduct.invoicePreview)}
        {renderEmailExcessInvoicePreview(userProduct.invoicePreview?.emailExcess)}
      </>
    );
  };
  const renderProductProcessing = (userProduct: UserProductTypes | null) => {
    if (!userProduct) return <></>;
    return (
      <div className={'md__payments__bg--warningLightNormal mb-4 border-b border-gray-200 py-5'}>
        <p className={'text-center ' + getItemLabelStyle()}>
          {t('MyRatePage.productProcessingLabel', {
            productName: userProduct.product.name,
          })}
        </p>
      </div>
    );
  };

  return (
    <div className="p-1 w-full md:w-1/2 xl:w-3/4">
      <PaymentsModal
        isOpen={cancelConfirmModalIsOpen}
        closeButton={true}
        onRequestClose={onCancelCancelPlan}
        style={extraWideModalStyles}
      >
        {getCancelConfirmDialog()}
      </PaymentsModal>
      <PaymentsModal
        isOpen={reactivateConfirmModalIsOpen}
        closeButton={true}
        onRequestClose={onCancelReactivatePlan}
        style={defaultModalStyles}
      >
        {getReactivateConfirmDialog()}
      </PaymentsModal>
      <div className="px-9 pt-4 pb-9">
        <div>
          {isProcessing && renderProductProcessing(userProductProcessing)}
          <div className={getItemListStyle()}>
            <p className={getItemLabelStyle()}>{t('MyRatePage.currentProduct')}</p>
            <p className={getItemLabelStyle()}>{`${product.name}`}</p>
          </div>
          <div className={getItemListStyle()}>
            <p className={getItemLabelStyle()}>{t('MyRatePage.currentProductInterval')}</p>
            <p className={getItemLabelStyle()}>
              {isAppSumo(me) ? t('BillingMode.unique') : t(getBillingModePlanText(userBillingMode))}
            </p>
          </div>
          <div className={getItemListStyle()}>
            <p className={getItemLabelStyle()}>{t('MyRatePage.currentProductPrice')}</p>
            <p className={getItemLabelStyle()}>{renderPrice(productPriceObj)}</p>
          </div>
          <div className={getItemListStyle()}>
            <div className={clsx(getItemLabelStyle(), 'flex items-center justify-end')}>
              {t('MyRatePage.excessCurrentProductPrice', { productType: productType })}
              {getTooltip(productPriceExcess)}
            </div>
            <p className={getItemLabelStyle()}>
              {renderMeteredPrice(productPriceExcess, productType)}
            </p>
          </div>
          {isActive && renderInvoicePreview(userProduct)}
        </div>
      </div>
      {getActionProductButton(userProducts, userProductActive)}
      <HelpContainer onClick={onRequestInfo} className="mt-5" />
    </div>
  );
};
